import { createApi, fetchBaseQuery } from '@reduxjs/toolkit/query/react';
import { CS_BASE_URL } from '../utils/ProjectUrls';
import { ReturnParameterValues } from '../schemas/ReturnParameterValues';
import { QueryRaw } from '../schemas/QueryRaw';
import { BasePartParameterValue } from '../schemas/BasePartParameterValue';
import { DataSheetResponse } from '../schemas/DataSheetResponse';
import { DataSheetRequest } from '../schemas/DataSheetRequest';
import { SupportInfo } from '../schemas/SupportInfo';
import { QueryParameterValues } from '../schemas/QueryParameterValues';
import { DynamicRequestBody } from '../schemas/DynamicRequestBody';
import { DynamicResponseBody } from '../schemas/DynamicResponseBody';
import { DynamicRequestBodyForBasePart } from '../schemas/DynamicRequestBodyForBasePart';
import { ReturnParameterValuesLite } from '../schemas/ReturnParameterValuesLite';

const QUERY = 'query';
const QUERY_RAW_PATH = `/${QUERY}/raw`;
export const QUERY_FILTER_PATH = `/${QUERY}/bp-filter`;
export const QUERY_BASE_PART_PATH = `/${QUERY}/base-part`;

export const QUERY_BASE_PART_FROM_RAW_PATH = `/${QUERY}/base-part-from-pn`;
export const QUERY_DATA_SHEETS = `/${QUERY}/data-sheets`;

export const QUERY_SUPPORT_INFO = `/${QUERY}/support-info`;

// 使用createApi创建一个新的API slice
// Use createApi to create a new API slice
export const querySlice = createApi({
    // 定义这个slice的reducer路径
    // Define the reducer path for this slice
    reducerPath: QUERY,
    // 设置API请求的基本URL
    // Set the base URL for API requests
    baseQuery: fetchBaseQuery({ baseUrl: CS_BASE_URL }),
    // 定义API endpoints
    // Define the API endpoints
    endpoints: (builder) => ({
        // 定义一个用于获取定义数据的query endpoint
        // Define a query endpoint for fetching definition data
        getQueryRaw: builder.query<ReturnParameterValues, QueryRaw>({
            // fulfill request body
            query: (body: QueryRaw) => ({
                url: QUERY_RAW_PATH,
                method: 'POST',
                body,
            }),
        }),
        getFilter: builder.query<DynamicResponseBody, DynamicRequestBody>({
            // fulfill request body
            query: (body: DynamicRequestBody) => ({
                url: `${QUERY_FILTER_PATH}/${encodeURIComponent(JSON.stringify(body))}`,
                method: 'GET',
            }),
        }),
        getBasePartWithLimitNew: builder.query<
            BasePartParameterValue[],
            DynamicRequestBodyForBasePart
        >({
            // fulfill request body
            query: (body: DynamicRequestBodyForBasePart) => ({
                url: `${QUERY_BASE_PART_PATH}/${encodeURIComponent(JSON.stringify(body))}`,
                method: 'GET',
            }),
        }),
        getBasePartByPn: builder.query<BasePartParameterValue[], string[]>({
            query: (pns: string[]) => ({
                url: `${QUERY_BASE_PART_FROM_RAW_PATH}`,
                method: 'POST',
                body: pns,
            }),
        }),
        getDatasheets: builder.query<DataSheetResponse, DataSheetRequest>({
            query: (body: DataSheetRequest) => ({
                url: QUERY_DATA_SHEETS,
                method: 'POST',
                body,
            }),
        }),
        getSupportInfoByPartNumber: builder.query<SupportInfo, string>({
            query: (partNumber: string) => ({
                url: `${QUERY_SUPPORT_INFO}/${partNumber}`,
                method: 'GET',
            }),
        }),
        getParameterValues: builder.query<ReturnParameterValues, QueryParameterValues>({
            query: (body: QueryParameterValues) => ({
                url: `${QUERY}/parameter-values`,
                method: 'POST',
                body,
            }),
        }),
    }),
});

// 从definitionSlice中导出自动生成的hook
// Export the auto-generated hook from the definitionSlice
export const {
    useLazyGetQueryRawQuery,
    useLazyGetSupportInfoByPartNumberQuery,
    useLazyGetParameterValuesQuery,
    useLazyGetBasePartWithLimitNewQuery,
    useGetFilterQuery,
    useLazyGetFilterQuery,
    useLazyGetBasePartByPnQuery,
} = querySlice;
